const by = (states, trigger, reachable, cause) => {
    states.push(cause && !reachable ? { trigger, reachable, cause } : { trigger, reachable });
};
/**
 * @param options the EFFECTIVE options — roles already resolved through any matched branch, since
 *   a branch reassigns roles and therefore changes every answer below.
 */
export function mcqReachability(d, options) {
    const states = [];
    const correctOptions = options.filter(o => o.role === 'correct');
    const wrongOptions = options.filter(o => o.role !== 'correct');
    /**
     * Which options a PRESENT targeted state claims, outside graded.
     *
     * `_absent` is excluded from this set deliberately, and it is not the same exclusion it used to
     * be. Absent states DO fire outside graded now (see `matchedTargeted`), but they do not close
     * the catch-all: the catch-all is what a selection falls through to, and "they did not pick X"
     * is true of a selection that also picked something nobody targeted. Only a PRESENT target
     * accounts for a pick, so only a present target can cover one.
     */
    const participationCovered = new Set(d.feedback.states
        .filter(s => s.type === 'targeted' && s.trigger.startsWith('option_') && !s.trigger.endsWith('_absent'))
        .map(s => s.trigger.slice('option_'.length))
        .filter(id => options.some(o => o.id === id)));
    // ── Mode gating, mirroring evaluateMCQ's first two branches ────────────────
    if (d.gradingMode === 'optional') {
        /**
         * OPTIONAL is INERT — see `targetedFiresIn`. The catch-all always fires and nothing else can:
         * no targeted state, no gate, no skip state of its own. It used to enumerate a blank against
         * the options and close the catch-all when they covered everything, which is gone with the
         * targeting that justified it.
         */
        by(states, 'any_response', true);
        for (const s of d.feedback.states) {
            if (s.trigger !== 'any_response')
                by(states, s.trigger, false, 'mode');
        }
        // `uncoveredWrong` is "what the catch-all still catches": every option, since nothing targets one.
        return { states, correct: { required: [], forbidden: [], optional: [] }, uncoveredWrong: options, uncoveredAbsent: [] };
    }
    // Reachable in graded AND survey: the empty-selection check runs before the survey branch.
    by(states, 'unanswered', true);
    if (d.gradingMode === 'survey') {
        /**
         * The catch-all closes when every option is covered — the SAME rule Default Incorrect follows
         * below, because `any_answer` plays the same role here.
         *
         * EVERY option, not just the wrong ones: nothing is correct in this mode, so nothing is
         * terminal and any pick can reach a targeted state. A blank cannot reach this branch at all,
         * having been caught as unanswered above, so a fully covered list leaves it nothing.
         */
        const uncovered = options.filter(o => !participationCovered.has(o.id));
        const reachable = options.length > 0 && uncovered.length > 0;
        by(states, 'any_answer', reachable, reachable ? undefined : options.length === 0 ? 'nothing-to-pick' : 'fully-covered');
        for (const id of participationCovered)
            by(states, `option_${id}`, true);
        /**
         * Absent states are reachable here too, and need a second option to be absent FROM: leaving
         * one out requires a non-empty selection, so with a single option there is nothing else to
         * pick instead. Multi-select only — a single pick cannot omit anything while still being a
         * pick.
         */
        for (const s of d.feedback.states) {
            if (s.type !== 'targeted' || !s.trigger.endsWith('_absent'))
                continue;
            const id = s.trigger.slice('option_'.length, -'_absent'.length);
            const exists = options.some(o => o.id === id);
            const cause = !d.allowMultiple ? 'mode' : !exists ? 'no-correct-option'
                : options.length < 2 ? 'nothing-else-to-select' : undefined;
            by(states, s.trigger, !cause, cause);
        }
        for (const t of ['correct', 'incorrect', 'any_response'])
            by(states, t, false, 'mode');
        // See the optional branch: the catch-all's own list, which is what the panel draws.
        return { states, correct: { required: [], forbidden: [], optional: [] }, uncoveredWrong: uncovered, uncoveredAbsent: [] };
    }
    by(states, 'any_answer', false, 'mode');
    by(states, 'any_response', false, 'mode');
    // ── Correct ────────────────────────────────────────────────────────────────
    // `isCorrect` needs EVERY correct option selected and no incorrect one, so two shapes make it
    // impossible.
    //
    // The single-select one is DEFENSIVE, not a live authoring hazard: the editor closes all three
    // routes to it — a role click in single-select demotes every other option, and switching from
    // multi to single collapses the static list AND every branch to the first correct. It can only
    // arrive as data written before those guards existed or edited outside the app, and the point of
    // handling it is that such data reports itself instead of rendering as an ordinary answer.
    const correctCause = correctOptions.length === 0 ? 'no-correct-option'
        : !d.allowMultiple && correctOptions.length > 1 ? 'multiple-correct-single-select'
            : undefined;
    by(states, 'correct', !correctCause, correctCause);
    // ── Targeted ───────────────────────────────────────────────────────────────
    const roleById = new Map(options.map(o => [o.id, o.role]));
    const targeted = d.feedback.states.filter(s => s.type === 'targeted');
    const coveredWrong = new Set();
    const coveredAbsent = new Set();
    for (const s of targeted) {
        if (s.trigger.endsWith('_absent')) {
            const id = s.trigger.slice('option_'.length, -'_absent'.length);
            // Fires only for a correct option that was left out — and leaving one out requires a
            // non-empty selection, so there has to be something else to pick instead.
            const isCorrectRole = roleById.get(id) === 'correct';
            const cause = 
            // Multi-select first: a single pick cannot omit an option while still being a pick, so
            // the trigger has nothing to describe whatever the role says.
            !d.allowMultiple ? 'nothing-else-to-select'
                : !isCorrectRole ? 'no-correct-option'
                    : options.length < 2 ? 'nothing-else-to-select' : undefined;
            by(states, s.trigger, !cause, cause);
            if (!cause)
                coveredAbsent.add(id);
            continue;
        }
        const id = s.trigger.slice('option_'.length);
        // Fires when a NON-correct option is picked. Always possible when the option exists in that
        // role: picking it alone cannot satisfy `isCorrect`, so nothing terminal gets in first.
        const ok = roleById.has(id) && roleById.get(id) !== 'correct';
        by(states, s.trigger, ok, ok ? undefined : 'no-correct-option');
        if (ok)
            coveredWrong.add(id);
    }
    // ── Default Incorrect ──────────────────────────────────────────────────────
    // The catch-all only runs when nothing targeted matched, so it needs at least one wrong answer
    // that no targeted state claims. Correct options count here only in multi-select, where one can
    // be omitted from an otherwise non-empty selection.
    const uncoveredWrong = wrongOptions.filter(o => !coveredWrong.has(o.id));
    const uncoveredAbsent = d.allowMultiple ? correctOptions.filter(o => !coveredAbsent.has(o.id)) : [];
    const incorrectReachable = uncoveredWrong.length > 0 || uncoveredAbsent.length > 0;
    by(states, 'incorrect', incorrectReachable, incorrectReachable ? undefined : 'fully-covered');
    return {
        states,
        // `required` in BOTH modes: it is what the student has to pick, and single-select has that too.
        // It used to be left empty for single-select, on the reasoning that there is no COMBINATION to
        // describe there — but the caller reads an empty `required` as "nothing can satisfy this", so
        // every single-select MCQ reported its own Correct state as unreachable. `forbidden` and
        // `optional` stay multi-only: picking one option cannot also pick a forbidden one.
        correct: d.allowMultiple
            ? { required: correctOptions, forbidden: options.filter(o => o.role === 'incorrect'), optional: options.filter(o => o.role === 'neutral') }
            : { required: correctOptions, forbidden: [], optional: [] },
        uncoveredWrong,
        uncoveredAbsent,
    };
}
/** Convenience for the common question. */
export function isReachable(r, trigger) {
    return r.states.find(s => s.trigger === trigger)?.reachable ?? false;
}
//# sourceMappingURL=mcq-reachability.js.map